import { computed, ComputedRef, getCurrentScope, isRef, onScopeDispose, reactive, Ref, ref, watch } from "vue";
import { useTimeoutFn } from "@vueuse/core";
import { isNullish } from "@tinyurldev/tu-ui-vue";

import useNotify from "@Composables/useNotify";
import type { PaddleCompactCheckoutContent } from "@Composables/usePaddle";
import useTranslation from "@Composables/useTranslation";
import BillingApi, {
  type BillingApi as BillingApiObject,
  type UnpaidTransactionContent,
} from "@Modules/billing/Api/BillingApi";
import type { Discount } from "@Modules/billing/Data/Models/DiscountModel";
import type { Invoice } from "@Modules/billing/Data/Models/InvoiceModel";
import {
  buildSubscriptionPaymentMethod,
  type SubscriptionPaymentMethod,
} from "@Modules/billing/Data/Models/SubscriptionPaymentMethod";
import XInvoiceStore, { PAY_ALL_PAYMENT_ID, type InvoiceStore } from "@Modules/billing/Store/XInvoiceStore";
import XPaymentMethodStore, { type PaymentMethodStore } from "@Modules/billing/Store/XPaymentMethodStore";
import type { SubscriptionData } from "@Modules/subscriptions/Data/Models/SubscriptionModel";
import BILLING_TYPE from "@Constants/billing-type";
import PADDLE_TRANSACTION_TYPE from "@Constants/paddle-transaction-type";
import SUBSCRIPTION_TYPE from "@Constants/subscription-type";
import { logError } from "@Helpers/logger";

/**
 * How many invoices the billing page shows before the "View All" link.
 */
export const LATEST_INVOICE_COUNT = 5;

/**
 * How long to wait before reloading the invoices once Paddle reports a payment
 * as completed, giving the Paddle webhook time to land on our side.
 */
export const PAYMENT_SETTLE_DELAY_MS = 5000;

/**
 * How many invoices a page of the transaction history holds.
 */
export const PER_PAGE = 12;

/**
 * A subscription as the subscription management table renders it.
 * The id is the subscription name because subscriptions carry no id of their own.
 */
export interface BillingSubscriptionRow {
  id: string
  name: string
  interval: string
  type: string
  billing_type: number
  paymentMethod: SubscriptionPaymentMethod
  hasActionButton: boolean
  hasTaxTooltip: boolean
}

/**
 * The banner the transaction table wears while the billable owes something, shaped the
 * way TuFeedbackBanner takes it.
 */
export interface TransactionFeedbackBanner {
  isClosable?: boolean
  feedbackButton?: { label?: string }
}

interface BillingViewModelState {
  requestedSubscriptionId: string | null
  hostedInvoiceId: string | number | null
  currentPage: number
}

export interface BillingViewModel {
  subscriptionRows: ComputedRef<BillingSubscriptionRow[]>
  requestedSubscriptionId: ComputedRef<string | null>
  updatedSubscriptionId: ComputedRef<string | null>
  downloadableInvoices: ComputedRef<Invoice[]>
  latestInvoices: ComputedRef<Invoice[]>
  invoicesReady: ComputedRef<boolean>
  currentPage: ComputedRef<number>
  pageCount: ComputedRef<number>
  pagedInvoices: ComputedRef<Invoice[]>
  discount: ComputedRef<Discount | null>
  couponErrors: ComputedRef<Record<string, string>>
  vatIdErrors: ComputedRef<Record<string, string>>
  payingInvoiceId: ComputedRef<string | number | null>
  hostedInvoiceId: ComputedRef<string | number | null>
  loadInvoices: () => Promise<boolean>
  updatePaymentMethod: (subscriptionId: string) => Promise<void>
  downloadInvoice: (invoice: Invoice, type: string) => void
  payInvoice: (invoice: Invoice) => Promise<void>
  payAllUnpaid: (billingType: number, handleStripeSession: HandleStripeSession) => Promise<void>
  openHostedInvoice: (invoice: Invoice) => Promise<void>
  loadDiscount: () => Promise<boolean>
  redeemCoupon: (code: string) => Promise<boolean>
  submitVatId: (vatId: string) => Promise<boolean>
  setPage: (page: number) => void
  reset: () => void
}

/**
 * Takes the stripe checkout session of the Pay All flow from here to the stripe checkout.
 * The single user page hands in `useSubscription().handleStripeSubscription`.
 */
export type HandleStripeSession = (session: UnpaidTransactionContent) => void | Promise<void>

export interface BillingViewModelOptions {
  /** Org slug ref — null/absent means single-user context; drives BillingApi URL scoping */
  orgSlug?: Ref<string | null>
  /** Raw subscriptions, already resolved per context by the controller */
  subscriptions: Ref<SubscriptionData[]>
  /** The api object. Injectable so tests can pass BillingApi().setRouter(FakeRouter(...)) */
  billingApi?: BillingApiObject
  /** Injectable for tests; defaults to the real pinia store */
  paymentMethodStore?: PaymentMethodStore
  /** Injectable for tests; defaults to the real pinia store */
  invoiceStore?: InvoiceStore
  /**
   * usePaddle() can only be instantiated in component setup (it calls useRouter/useLegacyStore),
   * so the controller passes openPaddleCompactCheckoutByTransaction in
   */
  openCompactCheckout: (transaction: string, content: PaddleCompactCheckoutContent) => void
  /** Full-page navigation for invoice downloads; injectable so tests don't touch window.location */
  navigate?: (url: string) => void
  /** Opens the provider's own invoice page; injectable so tests don't touch window.open */
  openWindow?: (url: string) => void
  /**
   * Called once a settled payment has been reloaded into the table, for the data the
   * view model does not own. The organization page refetches its own billing here,
   * where the single user gets the same from the legacy state refresh.
   */
  afterPaymentSettled?: () => Promise<void> | void
  /**
   * Called once something the billable itself carries has been saved, so the page can
   * pick the stored value back up instead of showing what it had before the save.
   */
  afterBillableChanged?: () => Promise<void> | void
}

/**
 * Returns the subscriptions of the given type, oldest first.
 */
function subscriptionsOfType(subscriptions: SubscriptionData[], type: string): SubscriptionData[] {
  return subscriptions
    .filter((subscription) => subscription.type === type)
    .sort((a, b) => new Date(a.created_at ?? 0).getTime() - new Date(b.created_at ?? 0).getTime());
}

/**
 * The wording the interval column renders under. A yearly subscription is billed
 * annually and everything else, a subscription with no interval of its own included,
 * is billed monthly.
 */
function billingInterval(interval: string | null | undefined): string {
  return interval === "yearly" ? "annual" : "monthly";
}

/**
 * Groups the subscriptions the way the table renders them: the default subscription first,
 * then shopping cart subscriptions, then addons, each group oldest first.
 */
function groupAndMapSubscriptions(subscriptions: SubscriptionData[]): BillingSubscriptionRow[] {
  return [
    ...subscriptionsOfType(subscriptions, SUBSCRIPTION_TYPE.DEFAULT),
    ...subscriptionsOfType(subscriptions, SUBSCRIPTION_TYPE.SHOPPING_CART),
    ...subscriptionsOfType(subscriptions, SUBSCRIPTION_TYPE.ADDON),
  ].map((subscription) => ({
    id: subscription.name,
    name: subscription.name,
    interval: billingInterval(subscription.interval),
    type: subscription.type,
    billing_type: subscription.billing_type,
    paymentMethod: buildSubscriptionPaymentMethod(subscription),
    hasActionButton: !(
      subscription.billing_type === BILLING_TYPE.STRIPE &&
      subscription.type === SUBSCRIPTION_TYPE.SHOPPING_CART
    ),
    hasTaxTooltip: subscription.billing_type === BILLING_TYPE.PADDLE,
  }));
}

/**
 * Puts the payment method of a completed checkout on the row it belongs to, so the table shows
 * the new card right away instead of waiting for the state refresh that follows the checkout.
 *
 * The rows are left alone when the checkout carries no payment method at all; a wallet payment
 * does come through, and is displayed by its brand alone.
 */
function applyUpdatedPaymentMethod(
  rows: BillingSubscriptionRow[],
  subscriptionId: string | null,
  paymentMethod: SubscriptionPaymentMethod | null,
): BillingSubscriptionRow[] {
  if (isNullish(subscriptionId) || isNullish(paymentMethod)) {
    return rows;
  }

  return rows.map((row) => (row.id === subscriptionId ? { ...row, paymentMethod } : row));
}

/**
 * Holds the logic of the Billing and Payment page and its transaction history subpage.
 *
 * The view model is context agnostic. The controller resolves the org slug and the
 * subscriptions for the context it renders, and passes them in.
 */
export default function BillingViewModel(options: BillingViewModelOptions): BillingViewModel {
  if (!isRef(options.subscriptions)) {
    throw new Error("BillingViewModel requires subscriptions to be a ref");
  }

  const orgSlug = options.orgSlug ?? ref(null);
  const subscriptions = options.subscriptions;
  const billingApi = options.billingApi ?? BillingApi();
  const store = options.paymentMethodStore ?? XPaymentMethodStore();
  const invoiceStore = options.invoiceStore ?? XInvoiceStore();
  const openCompactCheckout = options.openCompactCheckout;
  const navigate = options.navigate ?? ((url: string) => { window.location.assign(url); });
  const openWindow = options.openWindow ?? ((url: string) => { window.open(url, "_blank", "noopener"); });
  const afterPaymentSettled = options.afterPaymentSettled ?? null;
  const afterBillableChanged = options.afterBillableChanged ?? null;
  const { notifyError, notifySuccess } = useNotify();
  const { __ } = useTranslation();

  const state = reactive<BillingViewModelState>({
    requestedSubscriptionId: null,
    hostedInvoiceId: null,
    currentPage: 1,
  });

  const that: BillingViewModel = {
    subscriptionRows: computed(() => applyUpdatedPaymentMethod(
      groupAndMapSubscriptions(subscriptions.value),
      store.updatedSubscriptionId,
      store.updatedPaymentMethod,
    )),
    requestedSubscriptionId: computed(() => state.requestedSubscriptionId),
    updatedSubscriptionId: computed(() => store.updatedSubscriptionId),

    downloadableInvoices: computed(() => billingApi.invoices.value.filter((invoice) => !invoice.hasZeroTotal())),
    latestInvoices: computed(() => that.downloadableInvoices.value.slice(0, LATEST_INVOICE_COUNT)),
    invoicesReady: billingApi.invoicesReady,

    currentPage: computed(() => state.currentPage),
    pageCount: computed(() => Math.max(1, Math.ceil(that.downloadableInvoices.value.length / PER_PAGE))),
    pagedInvoices: computed(() => {
      const start = (state.currentPage - 1) * PER_PAGE;

      return that.downloadableInvoices.value.slice(start, start + PER_PAGE);
    }),

    discount: billingApi.discount,
    couponErrors: billingApi.couponErrors,
    vatIdErrors: billingApi.vatIdErrors,

    // The row stays held from the moment its checkout opens until the reloaded table
    // has confirmed the payment, so a completed checkout does not offer Pay Now again
    // while the provider's webhook is still landing.
    payingInvoiceId: computed(() => invoiceStore.pendingInvoiceId ?? invoiceStore.paidInvoiceId),

    hostedInvoiceId: computed(() => state.hostedInvoiceId),

    loadInvoices,
    updatePaymentMethod,
    downloadInvoice,
    payInvoice,
    payAllUnpaid,
    openHostedInvoice,
    loadDiscount,
    redeemCoupon,
    submitVatId,
    setPage,
    reset,
  };

  watch(that.downloadableInvoices, () => { state.currentPage = 1; });

  // Reloads the table once a payment goes through, so the invoice picks up its paid
  // status and drops its pay button. The wait is owned by the view model's scope, so it
  // stops with the page and a second payment inside the delay restarts it instead of
  // reloading twice.
  const settlePayment = useTimeoutFn(async () => {
    await loadInvoices();

    if (afterPaymentSettled !== null) {
      await afterPaymentSettled();
    }

    invoiceStore.resetInvoicePayment();
    notifySuccess({ text: __("settings_billing_page.transaction.payment_success") });
  }, PAYMENT_SETTLE_DELAY_MS, { immediate: false });

  if (getCurrentScope() !== undefined) {
    onScopeDispose(() => settlePayment.stop());
  }

  watch(() => invoiceStore.paidInvoiceId, (paidInvoiceId) => {
    if (paidInvoiceId === null) {
      return;
    }

    settlePayment.start();
  });

  return that;

  /**
   * Loads the billable's invoices for the current context.
   */
  async function loadInvoices(): Promise<boolean> {
    return billingApi.fetchInvoices(orgSlug.value);
  }

  /**
   * Opens the Paddle checkout that updates the payment method of the given subscription.
   */
  async function updatePaymentMethod(subscriptionId: string): Promise<void> {
    const row = that.subscriptionRows.value.find((subscription) => subscription.id === subscriptionId);

    if (row === undefined) {
      logError("The selected subscription is missing.");

      return;
    }

    store.resetPaymentMethodUpdate();
    store.beginPaymentMethodUpdate(subscriptionId);
    state.requestedSubscriptionId = subscriptionId;

    try {
      const content = await billingApi.fetchPaymentMethodCheckoutSession(orgSlug.value, {
        subscription: row.id,
        subscription_type: row.type,
        billing_type: row.billing_type,
      });

      if (content === null) {
        store.abortPaymentMethodUpdate();

        return;
      }

      openCompactCheckout(PADDLE_TRANSACTION_TYPE.PAYMENT_METHOD_UPDATE, content);
    } catch {
      store.abortPaymentMethodUpdate();
    } finally {
      state.requestedSubscriptionId = null;
    }
  }

  /**
   * Downloads the invoice or its receipt through a full page navigation.
   */
  function downloadInvoice(invoice: Invoice, type: string): void {
    navigate(`${invoice.downloadUrl}/?type=${type}`);
  }

  /**
   * Pays a single unpaid invoice, through the gateway the invoice was billed on.
   *
   * The row stays busy until the checkout completes, errors out or is dismissed; the
   * paddle events reach the store, which the paid watcher above reacts to.
   */
  async function payInvoice(invoice: Invoice): Promise<void> {
    if (invoiceStore.pendingInvoiceId !== null) {
      return;
    }

    invoiceStore.beginInvoicePayment(invoice.id);

    try {
      const content = await billingApi.payInvoice(orgSlug.value, invoice.id);

      if (content === null) {
        invoiceStore.abortInvoicePayment();

        return;
      }

      if (content.billing_type === BILLING_TYPE.STRIPE) {
        if (isNullish(content.redirect_url)) {
          throw new Error("The stripe invoice payment carries no redirect url.");
        }

        // The row keeps its spinner, the browser is leaving the page anyway.
        navigate(content.redirect_url);

        return;
      }

      openCompactCheckout(PADDLE_TRANSACTION_TYPE.UNPAID_TRANSACTION_PAYMENT, content);
    } catch (error) {
      logError(`Invoice payment failed: ${String(error)}`);
      notifyError();

      invoiceStore.abortInvoicePayment();
    }
  }

  /**
   * Settles every unpaid transaction at once, through the given gateway.
   *
   * The pending payment is armed before the session is requested, so every pay button is
   * disabled while it loads and a second click cannot open a second session. The sentinel
   * matches no invoice row, so no row shows a spinner. For a paddle billable it stays armed,
   * which is what lets the completed checkout watcher above reload the table.
   */
  async function payAllUnpaid(billingType: number, handleStripeSession: HandleStripeSession): Promise<void> {
    if (invoiceStore.pendingInvoiceId !== null) {
      return;
    }

    invoiceStore.beginInvoicePayment(PAY_ALL_PAYMENT_ID);

    const session = await billingApi.fetchUnpaidTransactionCheckoutSession(orgSlug.value, billingType);

    if (session === null) {
      invoiceStore.abortInvoicePayment();
      logError("Payment for all pending transactions failed.");
      notifyError({ text: __("transactions.create_failed") });

      return;
    }

    if (session.error) {
      invoiceStore.abortInvoicePayment();
      notifyError({ text: session.error.message });

      return;
    }

    const content = session.data ?? null;

    if (content === null) {
      invoiceStore.abortInvoicePayment();

      return;
    }

    if (billingType === BILLING_TYPE.STRIPE) {
      await handleStripeSession(content);
      invoiceStore.abortInvoicePayment();

      return;
    }

    openCompactCheckout(PADDLE_TRANSACTION_TYPE.UNPAID_TRANSACTION_PAYMENT, content);
  }

  /**
   * Opens the invoice page the provider hosts itself, in a new tab. The address is asked
   * for on the click rather than read off the row, because the provider's page expires,
   * and the row is held while the answer is on its way.
   */
  async function openHostedInvoice(invoice: Invoice): Promise<void> {
    if (!invoice.hasHostedInvoiceUrl()) {
      logError("The invoice carries no hosted invoice page.");

      return;
    }

    state.hostedInvoiceId = invoice.id;

    const hostedUrl = await billingApi.fetchHostedInvoiceUrl(orgSlug.value, invoice.id);

    state.hostedInvoiceId = null;

    if (hostedUrl === null) {
      return;
    }

    openWindow(hostedUrl);
  }

  /**
   * Loads the discount the billable currently receives.
   */
  async function loadDiscount(): Promise<boolean> {
    return billingApi.fetchDiscount(orgSlug.value);
  }

  /**
   * Redeems a coupon code, and reads the discount back so the banner shows what the
   * code was worth.
   */
  async function redeemCoupon(code: string): Promise<boolean> {
    const redeemed = await billingApi.redeemCoupon(orgSlug.value, code);

    if (!redeemed) {
      return false;
    }

    await loadDiscount();
    notifySuccess({ text: __("settings_billing_page.coupon.accepted") });

    return true;
  }

  /**
   * Saves the VAT id the billable's invoices are issued against, and reads the billable
   * back so the field keeps showing what is stored.
   */
  async function submitVatId(vatId: string): Promise<boolean> {
    const saved = await billingApi.updateVatId(orgSlug.value, vatId);

    if (!saved) {
      return false;
    }

    notifySuccess({ text: __("settings_billing_page.vat_id.updated") });

    if (afterBillableChanged !== null) {
      await afterBillableChanged();
    }

    return true;
  }

  /**
   * Shows the given page of the transaction history.
   */
  function setPage(page: number): void {
    state.currentPage = page;
  }

  /**
   * Clears the payment method update badge. The controller calls it on mount so a
   * badge from an earlier visit does not stick around.
   */
  function reset(): void {
    settlePayment.stop();
    store.resetPaymentMethodUpdate();
  }
}
